/**
 * The organization a card verification (3DS) return belongs to (#2602).
 *
 * BuyWithCardOnFileCard puts `?org=<active Clerk organization id>` on the
 * return URL it hands Stripe. If another tab switched the console while the
 * bank asked the founder to verify, the return page reads the purchase as the
 * wrong organization and gets not found; this is how it knows which
 * organization to offer a switch to.
 *
 * The route's loader records it here, before it scrubs the address. Module
 * level on purpose: the switch makes QueryCacheAuthReset remount the whole
 * router subtree, and by then the address no longer carries `?org=`, so
 * nothing held in a component or the address would survive it. The same
 * reason BillingOrgLinkGate keeps its `pendingSwitch` at module level.
 *
 * A full reload loses the module too, after the address was scrubbed, so the
 * organization is also kept in sessionStorage by purchase id and read back
 * from there. Storage that throws (private mode, blocked site data) only
 * loses the reload case; the module map still covers the switch.
 */
import { BILLING_ORG_REF_PATTERN } from "./billingOrgRef";

const returnOrgs = new Map<string, string>();
const STORAGE_PREFIX = "forged.manualPurchaseReturnOrg.";

/** Keeps a well-formed `org` from the return's query string, by purchase id.
 * Reads that one parameter and nothing else: Stripe's own parameters (among
 * them the client secret) are never looked at. A malformed `org` is ignored,
 * and so is a missing one, so a loader that runs again on the already
 * scrubbed address never forgets the organization it recorded the first time. */
export function rememberManualPurchaseReturnOrg(purchaseId: string, search: string) {
  const org = new URLSearchParams(search).get("org");
  if (!purchaseId || !org || !BILLING_ORG_REF_PATTERN.test(org)) return;
  returnOrgs.set(purchaseId, org);
  try {
    window.sessionStorage.setItem(STORAGE_PREFIX + purchaseId, org);
  } catch {
    // Storage unavailable: the module map still covers the org switch.
  }
}

/** The organization recorded for this purchase's return, if any. */
export function manualPurchaseReturnOrg(purchaseId: string): string | undefined {
  const recorded = returnOrgs.get(purchaseId);
  if (recorded) return recorded;
  try {
    const stored = window.sessionStorage.getItem(STORAGE_PREFIX + purchaseId);
    if (stored && BILLING_ORG_REF_PATTERN.test(stored)) return stored;
  } catch {
    // Storage unavailable: nothing survived the reload.
  }
  return undefined;
}

/** Tests only: forget what earlier tests recorded. */
export function resetManualPurchaseReturnOrgsForTests() {
  returnOrgs.clear();
  try {
    for (const key of Object.keys(window.sessionStorage)) {
      if (key.startsWith(STORAGE_PREFIX)) window.sessionStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable: nothing to forget.
  }
}
